// src/components/stateFaqs.js
//
// Derives state-specific FAQ entries from the structured stateData object.
// Used by StatePage.jsx to render the FAQ block AND the FAQPage schema.org
// JSON-LD payload for rich-result eligibility.
//
// Each FAQ has a `q` (question) and `a` (plain-text answer ≤ 300 chars so
// it fits inside Google's rich-result preview). Schema.org allows HTML in
// the answer text but the rich result truncates anything fancy, so we
// keep the answers as clean prose.
//
// The generator deliberately produces 5-7 FAQs per state by combining
// (a) one "how hard is the water" question (always)
// (b) one "best time of year to clean" question (always)
// (c) one "how much does it cost" question (always)
// (d) one "common streak / residue cause" question (uses top contaminant if any)
// (e) one "what's a [tier] water market" follow-up (always)
// (f) one "do I need a professional" question (always)
// (g) one climate-specific question if climate.extremes is present
//
// This produces consistent FAQ coverage across all 50 states without
// hand-writing 250+ questions, while each FAQ is specific enough to the
// state to feel non-generic.

import { STATE_PUBLISHED_HARDNESS } from '../data/statePublishedHardness.js';
import { hardnessKey } from '../data/water.js';

function tierExplain(tier) {
  // Plain-English explanation of what each hardness tier means for cleaning
  switch (tier) {
    case 'soft':
      return 'soft, meaning municipal water leaves minimal mineral residue when it dries on glass';
    case 'moderate':
      return 'moderate, meaning municipal water leaves visible spotting on dark glass and shows lower-sash residue over time';
    case 'hard':
      return 'hard, meaning municipal water consistently leaves visible mineral spots and requires acid-rinse protocols on long-residence glass';
    case 'very hard':
      return 'very hard, meaning municipal water deposits noticeable mineral residue every wash cycle and accelerates the long-term etching problem';
    case 'extremely hard':
      return 'extremely hard, meaning municipal water deposits aggressive mineral residue on every exposed pane and most cleaners run deionized rinse exclusively';
    default:
      return 'in the moderate range typical for most US markets';
  }
}

function topContaminant(stateData) {
  if (!stateData?.regionalContaminants?.length) return null;
  // Prioritize 'severe' > 'high' > 'moderate' > 'mild'
  const order = { severe: 4, high: 3, moderate: 2, mild: 1 };
  const sorted = [...stateData.regionalContaminants].sort(
    (a, b) => (order[b.severity] || 0) - (order[a.severity] || 0)
  );
  return sorted[0];
}

export function generateStateFaqs(stateData) {
  if (!stateData) return [];
  const faqs = [];
  const stateName = stateData.name;
  const wp = stateData.waterProfile || {};
  const cal = stateData.cleaningCalendar || {};
  const topC = topContaminant(stateData);
  const topCity = stateData.topCities?.[0]?.name;

  // 1. Water hardness — always include.
  // s136 (DECISIONS #58): quote what the state's covered utilities PUBLISH
  // (statePublishedHardness.js, generated from the rankings), never the planning
  // waterProfile range as if it were measured (#22). No published figure → the
  // answer says so and gives the planning range labelled as a planning estimate.
  const pub = STATE_PUBLISHED_HARDNESS[stateData.slug];
  if (pub && pub.n) {
    const span = pub.lo === pub.hi ? `${pub.lo} mg/L` : `${pub.lo} to ${pub.hi} mg/L`;
    const W = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
    const who = pub.n === 1 ? `The one utility we cover in ${stateName} that publishes hardness reports` : `The ${W[pub.n] || pub.n} utilities we cover in ${stateName} that publish hardness report`;
    faqs.push({
      q: `How hard is the water in ${stateName}?`,
      a: `${who} ${span} (as CaCO₃). The middle of those cities sits near ${pub.med} mg/L, which is ${tierExplain((hardnessKey(pub.med) || '').replace('-', ' '))}. Each city page gives its own utility's figure.`,
    });
  } else if (wp.hardnessLow && wp.hardnessHigh) {
    faqs.push({
      q: `How hard is the water in ${stateName}?`,
      a: `None of the ${stateName} utilities we cover publishes a hardness figure we could read, so we have no measured number to give. As a planning estimate only, municipal supplies here fall around ${wp.hardnessLow}–${wp.hardnessHigh} mg/L (CaCO₃). Ask your utility for its report.`,
    });
  }

  // 2. Best time of year to clean
  if (cal.spring || cal.summer || cal.fall) {
    const bestSeason = cal.fall
      ? `fall — ${cal.fall.toLowerCase()}`
      : cal.spring
        ? `spring — ${cal.spring.toLowerCase()}`
        : `summer — ${cal.summer.toLowerCase()}`;
    faqs.push({
      q: `When is the best time of year to clean windows in ${stateName}?`,
      a: `In ${stateName}, the working operator's calendar typically favors ${bestSeason} For a full seasonal breakdown, see the cleaning calendar section on this page.`.slice(0, 380),
    });
  }

  // 3. Cost question — generic answer that links to the calculator
  faqs.push({
    q: `How much does window cleaning cost in ${stateName}?`,
    a: `Residential window cleaning in ${stateName} typically runs $8–18 per pane or $200–500 for a standard single-family house exterior, depending on metro pricing, story height, screen condition, and frame type. Use our cost estimator for a calibrated quote for your home.`,
  });

  // 4. Streak/residue cause — uses top contaminant if available
  if (topC) {
    faqs.push({
      q: `Why do my windows look dirty so quickly in ${stateName}?`,
      a: `The dominant residue problem in ${stateName} is ${topC.name.toLowerCase()} (${topC.season}). ${topC.notes} Regular cleaning intervals tied to the season the contaminant peaks will significantly extend how long a wash holds.`.slice(0, 380),
    });
  } else {
    faqs.push({
      q: `Why do my windows look dirty so quickly in ${stateName}?`,
      a: `The most common causes of fast-returning residue are airborne contaminants (pollen, dust, smoke), hard-water mineral deposits from irrigation overspray, and incomplete drying of the wash itself. The state-specific diagnostic on this page covers the patterns most common in ${stateName}.`,
    });
  }

  // 5. Professional vs DIY
  faqs.push({
    q: `Do I need a professional to clean my windows in ${stateName}?`,
    a: `Single-story homes with accessible glazing can be cleaned by homeowners using basic squeegee technique and the right solution. Multi-story houses, post-2010 coated glass, hard-water markets, and screens-plus-tracks work usually pay for themselves with a professional. See our hiring checklist below.`,
  });

  // 6. Climate-specific question if extremes are interesting
  if (stateData.climate?.extremes && stateData.climate.extremes.length > 20) {
    const ex = stateData.climate.extremes;
    faqs.push({
      q: `What's special about cleaning windows in ${stateName}'s climate?`,
      a: `${ex.charAt(0).toUpperCase() + ex.slice(1)} These conditions shape what a cleaner needs to know about scheduling, technique, and timing. The cleaning calendar on this page reflects this rhythm.`.slice(0, 380),
    });
  }

  // 7. Best city / "near me" funnel
  if (topCity) {
    faqs.push({
      q: `Where can I find a window cleaner in ${topCity}, ${stateName}?`,
      a: `${topCity} is the largest market in ${stateName} and has the deepest concentration of professional window-cleaning services. Our \"Find a Cleaner\" page takes waitlist sign-ups while the local pro directory is built; meanwhile, read the ${topCity} section of this page for the city-specific water and cleaning context.`,
    });
  }

  return faqs;
}

// Build the FAQPage JSON-LD schema.org payload for the rich-result.
// Returned as a stringified JSON object ready to drop into <script type="application/ld+json">.
export function faqJsonLd(faqs) {
  if (!faqs || !faqs.length) return null;
  const payload = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };
  return JSON.stringify(payload);
}
